import { ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Container, Stack, Typography } from '@mui/material';
import { CalendarDots } from '@phosphor-icons/react';
import { addDays, isSameDay, startOfDay, Locale } from 'date-fns';
import { ActivityInstance } from '@api';
// (i18n hanteras av wrappers; WeekView tar emot färdigöversatta strängar)
import { CalendarContext } from '@/contexts/CalendarContext';
import { getChildActivityStatus } from '@/components/child';
import CreateActivityFab from '@/components/layout/CreateActivityFab';
import VoiceActivityCreator from '@/components/voice/VoiceActivityCreator';
import WeekDayCard from './WeekDayCard';
import WeekProgressBar from './WeekProgressBar';
import type { WeekTokens } from './weekTokens';

/**
 * WeekView-flaggor — styr de ADDITIVA lager som får skilja per persona ovanpå
 * den gemensamma dagskorts-strukturen. Själva layouten/ordningen/korten/
 * completed-signalen/aktivitetsraden är ALLTID identiska (drivs inte av flaggor).
 */
export type WeekViewFlags = {
  /** Hopfällbara dagar (teen). false = dagar alltid öppna (barn/adult). */
  collapsibleDays: boolean;
  /**
   * Klar-räknare {dayDone}/{dayTotal} i dagskort-headern. FRIKOPPLAD från
   * collapsibleDays så att adult (alltid öppna dagar) får en aggregerad
   * completion-signal per dag. Teen: true (behåller sin räknare). Barn: false.
   */
  showDayCount: boolean;
  /** Teens veckoframstegs-bar överst (additivt statuslager). */
  showWeekProgress: boolean;
  /** Teens guldstjärna på 100%-klara dagar. */
  showDayStar: boolean;
  /** Teens status-dots på hopfällda dagskort. */
  showStatusDots: boolean;
  /** Barnets emoji-ikoner i aktivitetsraderna. */
  showActivityEmoji: boolean;
  /** Barnets EmbeddedCompanion-maskot överst. */
  showCompanion: boolean;
  /** Teens pulserande glow runt dagens kort. */
  todayGlow: boolean;
};

type WeekViewProps = {
  /** Vecko-titel (i18n-översatt av wrappern). */
  title: string;
  /** Måndagen som vyns 7 dagar utgår från. Adult: vald vecka; barn/teen: innevarande. */
  weekStart: Date;
  tokens: WeekTokens;
  flags: WeekViewFlags;
  /** Navigerings-slot: adult skickar WeekSelect, barn/teen en statisk WeekNavLabel. */
  weekNav: ReactNode;
  /** Barnets maskot-nod (EmbeddedCompanion) — renderas när flags.showCompanion. */
  companion?: ReactNode;
  /** Etiketter (i18n-översatta av wrappern). */
  todayLabel: string;
  noActivitiesLabel: string;
  weekProgressLabel: string;
  /** "Nu"-etikett för nu-markören i dagens kort. */
  nowLabel: string;
  /** date-fns locale för dag/datum-formatering. */
  locale: Locale;
  /**
   * Teen: avgör om en aktivitets stjärnvärde väntar på förälderns besked. Utelämnad
   * (barn/adult) = ingen markering. Wrappern äger frågan (usePendingStarValues) så att
   * vyn inte hämtar data för personas som aldrig har väntande värden.
   */
  isStarValuePending?: (instance: ActivityInstance) => boolean;
  /** Färdigöversatt etikett för väntan-markeringen. */
  starValuePendingLabel?: string;
};

const GLOW_KEYFRAMES = (accent: string) => `
@keyframes teenWeekGlow {
  0%, 100% { box-shadow: 0 0 12px ${accent}4D; }
  50% { box-shadow: 0 0 20px ${accent}80; }
}
`;

/**
 * WeekView — EN gemensam veckovy för alla tre personas (barn/teen/adult).
 *
 * Fast struktur (konsistens-kontraktet, aldrig flagg-styrd):
 *   (1) header med vecko-titel
 *   (2) veckonavigerings-slot (samma plats)
 *   (3) valfritt statuslager (teens progress)
 *   (4) de 7 dagarna måndag→söndag som dagskort
 *   (5) FAB nere till höger (+ röst — nu för ALLA personas)
 *
 * Persona-skillnaderna är rena additiva lager via WeekViewFlags + tokens-värden.
 */
const WeekView = ({
  title,
  weekStart,
  tokens,
  flags,
  weekNav,
  companion,
  todayLabel,
  noActivitiesLabel,
  weekProgressLabel,
  nowLabel,
  locale,
  isStarValuePending,
  starValuePendingLabel,
}: WeekViewProps) => {
  const { weekInstances, setCalendarDialog } = useContext(CalendarContext);
  const today = new Date();
  const todayStart = startOfDay(today);
  const todayCardRef = useRef<HTMLDivElement | null>(null);

  const days = useMemo(() => {
    const monday = startOfDay(weekStart);
    return Array.from({ length: 7 }).map((_, i) => {
      const day = addDays(monday, i);
      const dayInstances = (weekInstances ?? [])
        .filter((inst) => isSameDay(new Date(inst.startDate), day))
        .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
      return { day, instances: dayInstances };
    });
  }, [weekInstances, weekStart]);

  const todayIndex = useMemo(
    () => days.findIndex(({ day }) => isSameDay(day, today)),
    [days, today],
  );

  // Teen: dagens kort expanderat som default; övriga hopfällda.
  const [expandedDay, setExpandedDay] = useState<number>(todayIndex);
  const [voiceOpen, setVoiceOpen] = useState(false);

  const weekTotal = useMemo(() => {
    const all = days.flatMap((d) => d.instances);
    const done = all.filter((i: ActivityInstance) => getChildActivityStatus(i) === 'done').length;
    return { done, total: all.length };
  }, [days]);

  // Scrolla dagens kort in i vy när vyn öppnas / veckan byts — men ENDAST om
  // "idag" finns i den visade veckan (adult kan ha bläddrat bort). Respekterar
  // prefers-reduced-motion (instant hopp i stället för mjuk scroll).
  useEffect(() => {
    if (todayIndex < 0) return;
    const el = todayCardRef.current;
    if (!el || typeof el.scrollIntoView !== 'function') return;
    const prefersReduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'center' });
    // weekStart i deps: adult som bläddrar tillbaka till innevarande vecka scrollar om.
  }, [todayIndex, weekStart.getTime()]);

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: tokens.pageBg }}>
      {flags.todayGlow && <Box component="style">{GLOW_KEYFRAMES(tokens.accent)}</Box>}

      <Container maxWidth="sm" sx={{ pb: 12, px: { xs: 2, sm: 3 } }}>
        <Stack spacing={2} pt={2}>
          {/* Barnets maskot (additivt) */}
          {flags.showCompanion && companion}

          {/* (1) Header med vecko-titel */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarDots size={22} weight="duotone" color={tokens.text} />
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.35rem',
                color: tokens.text,
                letterSpacing: '-0.02em',
              }}
            >
              {title}
            </Typography>
          </Box>

          {/* (2) Veckonavigerings-slot — samma plats överallt */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>{weekNav}</Box>

          {/* (3) Valfritt statuslager (teen) */}
          {flags.showWeekProgress && (
            <WeekProgressBar
              done={weekTotal.done}
              total={weekTotal.total}
              tokens={tokens}
              label={weekProgressLabel}
            />
          )}

          {/* (4) 7 dagar mån→sön som dagskort */}
          <Stack spacing={flags.collapsibleDays ? 1 : 1.25}>
            {days.map(({ day, instances }, index) => {
              const isToday = isSameDay(day, today);
              const isPast = startOfDay(day) < todayStart;
              return (
                <WeekDayCard
                  key={day.toISOString()}
                  rootRef={isToday ? todayCardRef : undefined}
                  day={day}
                  instances={instances}
                  tokens={tokens}
                  locale={locale}
                  isToday={isToday}
                  isPast={isPast}
                  now={today}
                  nowLabel={nowLabel}
                  collapsible={flags.collapsibleDays}
                  expanded={expandedDay === index}
                  onToggle={() => setExpandedDay((prev) => (prev === index ? -1 : index))}
                  showDayCount={flags.showDayCount}
                  showStatusDots={flags.showStatusDots}
                  showDayStar={flags.showDayStar}
                  showEmoji={flags.showActivityEmoji}
                  isStarValuePending={isStarValuePending}
                  starValuePendingLabel={starValuePendingLabel}
                  todayGlow={flags.todayGlow}
                  todayLabel={todayLabel}
                  noActivitiesLabel={noActivitiesLabel}
                  onActivityClick={(id) => setCalendarDialog([id])}
                />
              );
            })}
          </Stack>
        </Stack>
      </Container>

      {/* (5) FAB nere till höger — röstskapande PÅ för alla personas överallt. */}
      <CreateActivityFab onVoiceClick={() => setVoiceOpen(true)} />
      <VoiceActivityCreator
        open={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        onSaved={() => setVoiceOpen(false)}
      />
    </Box>
  );
};

export default WeekView;
